import { Component, computed, effect, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TrackerPhotoResultsResponse } from 'dashboard/components/visuals-dashboard/widgets/tracker-photo-results-widget/tracker-photo-results-data-response.interface';
import { IcePieSegmentsToDashboardSegmentsColorMap } from 'dashboard/constants/visuals-dashboard/ice-pie-segments-dashboard-segments-colormap';
import { VisualsDashboardWidgetSizingEnum } from 'dashboard/enums/visuals/visuals-dashboard-widget-sizing.enum';
import { DashboardWidget } from 'dashboard/interfaces/visuals/dashboard-widget.interface';
import { VisualDashboardDistributor } from 'dashboard/interfaces/visuals/visual-dashboard-distributor.interface';
import { VisualsDashboardDataService } from 'dashboard/services/visuals-dashboard/visualds-dashboard-data.service';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import {
    IceVisualsDashboardPieChartComponent,
    PieChartSegment,
    PieChartVariantEnum,
} from 'shared/components/ice-visuals-dashboard/ice-visuals-dashboard-pie-chart/ice-visuals-dashboard-pie-chart.component';

const ALL_DISTRIBUTORS = -1;

@Component({
    selector: 'app-tracker-photo-results-widget',
    imports: [IceVisualsDashboardPieChartComponent, TranslateModule],
    templateUrl: './tracker-photo-results-widget.component.html',
    styleUrl: './tracker-photo-results-widget.component.scss',
    providers: [VisualsDashboardDataService],
})
export class TrackerPhotoResultsWidgetComponent {
    widget = input.required<DashboardWidget>();
    visualsDashboardWidgetSizingEnum = VisualsDashboardWidgetSizingEnum;
    pieChartVariantEnum = PieChartVariantEnum;
    selectedDistributorId = input<number | null>();
    onLangChange = toSignal(this.translate.onLangChange);

    widgetId = computed(() => {
        const widget = this.widget();
        return widget.iceDashboardWidget.widgetId;
    });

    selectedSize = computed(() => {
        const widget = this.widget();
        return widget.selectedSize;
    });

    availableDistributors = computed<VisualDashboardDistributor[]>(() => {
        const data = this.dataService.data();
        if (!data) {
            return [];
        }

        const distributorMap = new Map<number, string>();
        data.ranges.forEach((range) => {
            if (!distributorMap.has(range.distributorId)) {
                distributorMap.set(range.distributorId, range.distributorName);
            }
        });

        const allEntry = distributorMap.get(ALL_DISTRIBUTORS);
        distributorMap.delete(ALL_DISTRIBUTORS);

        const sorted = Array.from(distributorMap, ([distributorId, distributorName]) => ({
            distributorId,
            distributorName,
        })).sort((entryA, entryB) => entryA.distributorName.localeCompare(entryB.distributorName));

        if (allEntry) {
            sorted.unshift({ distributorId: ALL_DISTRIBUTORS, distributorName: allEntry });
        }

        return sorted;
    });

    trackerPhotoSegments = computed(() => {
        const data = this.dataService.data();
        this.onLangChange(); // Re-evaluate on language change
        if (!data) {
            return [];
        }

        const distributorId = this.selectedDistributorId() ?? ALL_DISTRIBUTORS;

        const filteredRanges = data.ranges.filter((range) => range.distributorId === distributorId);

        const segments: PieChartSegment[] = filteredRanges
            .sort((rangeA, rangeB) => rangeA.displayOrder - rangeB.displayOrder)
            .map((range) => {
                const mappedColor =
                    IcePieSegmentsToDashboardSegmentsColorMap[range.colorCode as keyof typeof IcePieSegmentsToDashboardSegmentsColorMap];
                return {
                    label: this.translate.instant(`dashboard.visuals.widgets.trackerPhoto.rangeLabels.${range.title}`),
                    value: range.displayValue,
                    color: mappedColor || range.colorCode,
                };
            });

        return segments;
    });

    constructor(
        public dataService: VisualsDashboardDataService<TrackerPhotoResultsResponse>,
        private visualsDashboardService: VisualsDashboardService,
        private translate: TranslateService,
    ) {
        effect(() => {
            const availableDistributors = this.availableDistributors();
            const widgetHtmlId = this.widget().htmlId;
            if (widgetHtmlId && availableDistributors !== null) {
                this.visualsDashboardService.setAvailableDistributorsForHtmlId(widgetHtmlId, availableDistributors);
            }
        });
        effect(() => {
            const widgetId = this.widgetId();
            this.dataService.load(widgetId);
        });
    }
}
